import React, { useState, useEffect } from 'react';
import { Lock, KeyRound, Plus, Trash2, Eye, EyeOff, ShieldCheck, Unlock } from 'lucide-react';
import { dbStorage } from '../../storage/db';
import { liveSynth } from '../../core/audio/BinauralEngine';
import { CryptoEngine } from '../../core/crypto/cryptoEngine';

export const VaultEngine = () => {
  const [notes, setNotes] = useState([]);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [masterKey, setMasterKey] = useState('');
  const [decryptedMap, setDecryptedMap] = useState({});
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    loadNotes();
  }, []);

  const loadNotes = async () => {
    try {
      const records = await dbStorage.getAll('system_state');
      const vaultData = records.find(r => r.key === 'vault_notes');
      if (vaultData && Array.isArray(vaultData.value)) {
        setNotes(vaultData.value);
      } else {
        const fallback = localStorage.getItem('vault_notes');
        if (fallback) setNotes(JSON.parse(fallback));
      }
    } catch (e) {
      console.warn('Vault load fallback:', e);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!title.trim() || !content.trim()) return;
    setErrorMsg('');
    liveSynth.playClickSfx();

    try {
      let finalContent = content;
      let isEncrypted = false;

      if (masterKey.trim()) {
        finalContent = await CryptoEngine.encrypt(content, masterKey.trim());
        isEncrypted = true;
      }

      const newNote = {
        id: Date.now(),
        title,
        content: finalContent,
        isEncrypted,
        date: new Date().toLocaleDateString('fa-IR')
      };

      const updated = [newNote, ...notes];
      setNotes(updated);
      setTitle('');
      setContent('');

      await dbStorage.put('system_state', { key: 'vault_notes', value: updated });
      localStorage.setItem('vault_notes', JSON.stringify(updated));
      liveSynth.playSuccessSfx();
    } catch (err) {
      setErrorMsg('خطا در رمزنگاری یادداشت.');
    }
  };

  const toggleDecrypt = async (note) => {
    liveSynth.playClickSfx();
    if (decryptedMap[note.id]) {
      const next = { ...decryptedMap };
      delete next[note.id];
      setDecryptedMap(next);
      return;
    }

    if (!note.isEncrypted) {
      setDecryptedMap(prev => ({ ...prev, [note.id]: note.content }));
      return;
    }

    if (!masterKey.trim()) {
      setErrorMsg('برای باز کردن این سند، لطفاً ابتدا کلید رمز اصلی را در بالا وارد کنید.');
      return;
    }

    try {
      const plain = await CryptoEngine.decrypt(note.content, masterKey.trim());
      setDecryptedMap(prev => ({ ...prev, [note.id]: plain }));
      setErrorMsg('');
    } catch (err) {
      setErrorMsg(err.message || 'رمز اصلی اشتباه است.');
    }
  };

  const handleDelete = async (id) => {
    liveSynth.playClickSfx();
    const updated = notes.filter(n => n.id !== id);
    setNotes(updated);
    try {
      await dbStorage.put('system_state', { key: 'vault_notes', value: updated });
      localStorage.setItem('vault_notes', JSON.stringify(updated));
    } catch (err) {
      console.warn('Vault delete error:', err);
    }
  };

  return (
    <div className="space-y-6 pb-24 text-slate-100 max-w-2xl mx-auto text-right" dir="rtl">
      <div className="bg-slate-900/90 border border-sky-500/30 rounded-2xl p-5 shadow-xl backdrop-blur-md">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-sky-500/10 border border-sky-500/30 rounded-xl text-sky-400">
            <Lock className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-base font-bold text-sky-400">گاوصندوق رمزنگاری‌شده AES-256</h1>
            <p className="text-xs text-slate-400">حفاظت از کلیدها و اسناد با رمزنگاری پیشرفته سخت‌افزاری</p>
          </div>
        </div>
      </div>

      {/* نوار ورود کلید اصلی (Master Key) */}
      <div className="bg-slate-900/80 border border-sky-500/20 rounded-2xl p-4 space-y-2">
        <div className="flex items-center gap-2 text-xs font-bold text-sky-400">
          <KeyRound className="w-4 h-4" />
          <span>گذرواژه اصلی رمزنگاری (Master Passphrase):</span>
        </div>
        <input
          type="password"
          placeholder="گذرواژه محرمانه برای قفل/گشایش اسناد..."
          value={masterKey}
          onChange={(e) => setMasterKey(e.target.value)}
          className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-sm text-white focus:border-sky-400 focus:outline-none font-mono"
        />
        {errorMsg && <p className="text-xs text-rose-400 mt-1">{errorMsg}</p>}
      </div>

      <form onSubmit={handleSave} className="bg-slate-900/80 border border-slate-800 rounded-2xl p-4 space-y-3 shadow-lg">
        <input
          type="text"
          placeholder="عنوان سند محرمانه..."
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-sm text-white focus:border-sky-400 focus:outline-none"
        />
        <textarea
          placeholder="متن محرمانه، کلید API یا مختصات پروژه..."
          value={content}
          onChange={(e) => setContent(e.target.value)}
          rows={3}
          className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-sm text-white focus:border-sky-400 focus:outline-none"
        />
        <button
          type="submit"
          className="w-full py-2.5 bg-sky-400 hover:bg-sky-500 text-slate-950 font-bold text-sm rounded-xl flex items-center justify-center gap-2 cursor-pointer transition-colors"
        >
          <Plus className="w-4 h-4" />
          <span>رمزنگاری و ذخیره سند</span>
        </button>
      </form>

      <div className="space-y-3">
        {notes.map((note) => {
          const isDecrypted = Boolean(decryptedMap[note.id]);
          return (
            <div key={note.id} className="bg-slate-900/60 border border-slate-800 rounded-xl p-4 space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-bold text-slate-200">{note.title}</h3>
                  {note.isEncrypted && (
                    <span className="text-[10px] px-2 py-0.5 rounded-md bg-sky-500/10 text-sky-400 border border-sky-500/30">
                      AES-256
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-slate-500 font-mono">{note.date}</span>
                  <button
                    type="button"
                    onClick={() => toggleDecrypt(note)}
                    className="p-1.5 text-slate-400 hover:text-sky-400 transition-colors"
                    title={isDecrypted ? 'قفل کردن' : 'رمزگشایی'}
                  >
                    {isDecrypted ? <Unlock className="w-4 h-4 text-emerald-400" /> : <Eye className="w-4 h-4" />}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(note.id)}
                    className="p-1.5 text-slate-500 hover:text-rose-400 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {isDecrypted ? (
                <p className="text-xs text-emerald-300 whitespace-pre-wrap bg-slate-950/80 p-3 rounded-lg border border-emerald-500/30 font-mono">
                  {decryptedMap[note.id]}
                </p>
              ) : (
                <p className="text-xs text-slate-600 font-mono tracking-widest">
                  {note.isEncrypted ? '•••••••• [سند رمزنگاری‌شده AES-GCM] ••••••••' : '••••••••••••••••••••'}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default VaultEngine;
